"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import config from "../config"
import { useAuth } from "../context/AuthContext"
import { useNavigate } from "react-router-dom"

const AdminDashboard = () => {
    const { user, loading, isLoggedIn } = useAuth()
    const navigate = useNavigate()

    // State management
    const [users, setUsers] = useState([])
    const [events, setEvents] = useState([])
    const [bids, setBids] = useState([])
    const [loadingData, setLoadingData] = useState(true)
    const [error, setError] = useState(null)
    const [activeTab, setActiveTab] = useState("overview")
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [pageCount, setPageCount] = useState(10)
    const [searchTerm, setSearchTerm] = useState("")
    const [filterRole, setFilterRole] = useState("")
    const [selectedUsers, setSelectedUsers] = useState([])
    const [showUserModal, setShowUserModal] = useState(false)
    const [selectedUser, setSelectedUser] = useState(null)
    const [recentActivity, setRecentActivity] = useState([])
    const [systemStats, setSystemStats] = useState({
        totalRevenue: 0,
        newUsersThisMonth: 0,
        activeEvents: 0,
        pendingApprovals: 0,
        totalUsers: 0
    })
    const [userStats, setUserStats] = useState({
        totalUsers: 0,
        serviceProviderCount: 0,
        eventOrganizerCount: 0,
        eventAttendeeCount: 0,
        communityMemberCount: 0
    })

    // Check admin access
    useEffect(() => {
        if (!loading && (!isLoggedIn || user?.role !== "admin")) {
            navigate("/login")
            return
        }
    }, [user, loading, isLoggedIn, navigate])

    // Fetch admin data
    useEffect(() => {
        if (user?.role === "admin") {
            fetchAdminData()
        }
    }, [user, currentPage, pageCount, searchTerm, filterRole])

    const fetchAdminData = async () => {
        try {
            setLoadingData(true)
            const token = localStorage.getItem("access_token")
            const headers = { Authorization: `Bearer ${token}` }

            // Fetch users with pagination and filters
            const usersParams = {
                page: currentPage,
                page_size: pageCount,
                ...(searchTerm && { search: searchTerm }),
                ...(filterRole && { role: filterRole }),
            }

            // Fetch all data
            const [usersResponse, eventsResponse, bidsResponse, activityResponse] = await Promise.all([
                axios.get(`${config.API_BASE_URL}/users/all/`, { headers, params: { page: 1, page_size: 1000 } }),
                axios.get(`${config.API_BASE_URL}/events/all/`, { headers }),
                axios.get(`${config.API_BASE_URL}/bids/open/`, { headers }),
                axios.get(`${config.API_BASE_URL}/admin/activity/`, { headers }).catch(() => ({ data: [] })),
            ])

            // Handle users response
            const usersData = usersResponse.data || { results: [], count: 0, total_users: 0, new_users_this_month: 0 }
            setUsers(usersData.results || [])
            setTotalPages(Math.ceil((usersData.count || 0) / pageCount))

            // Calculate system stats and update user stats
            const stats = calculateSystemStats(
                usersData,  // Use the complete usersData object
                eventsResponse.data || [],
                bidsResponse.data || [],
                usersData.count || 0
            );
            setUserStats(stats);

            // Set system stats
            setSystemStats({
                totalRevenue: 0,
                newUsersThisMonth: usersData.new_users_this_month || 0,
                activeEvents: 0,
                pendingApprovals: 0,
                totalUsers: usersData.count || 0
            })

            // Handle other responses
            setEvents(eventsResponse.data || [])
            setBids(bidsResponse.data || [])
            setRecentActivity(activityResponse.data || [])

        } catch (error) {
            // console.error("Error fetching admin data:", error)
            setError("Failed to fetch admin data")
            // Set default empty values
            setUsers([])
            setEvents([])
            setBids([])
            setRecentActivity([])
            setSystemStats({
                totalRevenue: 0,
                newUsersThisMonth: 0,
                activeEvents: 0,
                pendingApprovals: 0,
                totalUsers: 0
            })
        } finally {
            setLoadingData(false);
        }

    }

    const calculateSystemStats = (usersData, eventsData, bidsData, totalUsers = 0) => {
        // Create timezone-aware dates
        const now = new Date()
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

        // Convert to ISO string to handle timezone
        const nowISO = now.toISOString()
        const thirtyDaysAgoISO = thirtyDaysAgo.toISOString()

        // Calculate user statistics first
        const stats = {
            totalUsers: totalUsers,
            serviceProviderCount: usersData?.results?.filter((u) => {
                const role = (u.role || '').toLowerCase()
                return role.includes('service provider') || role.includes('serviceprovider')
            }).length || 0,
            eventOrganizerCount: usersData?.results?.filter((u) => {
                const role = (u.role || '').toLowerCase()
                return role.includes('event organizer') || role.includes('eventorganizer')
            }).length || 0,
            eventAttendeeCount: usersData?.results?.filter((u) => {
                const role = (u.role || '').toLowerCase()
                return role.includes('event attendee') || role.includes('eventattendee')
            }).length || 0,
            communityMemberCount: usersData?.results?.filter((u) => {
                const role = (u.role || '').toLowerCase()
                return role.includes('community member') || role.includes('communitymember')
            }).length || 0,
        }

        // Count new users this month
        const newUsersThisMonth = usersData?.results?.filter(user => {
            // Convert user dates to ISO string for timezone handling
            const userCreated = new Date(user.created_at || user.date_joined).toISOString()
            return userCreated >= thirtyDaysAgoISO && userCreated <= nowISO
        }).length || 0

        // Set system stats
        setSystemStats({
            totalRevenue: bidsData.filter((b) => b.status === "accepted").reduce((sum, b) => sum + (b.price || 0), 0),
            activeEvents: eventsData.filter((e) => e.status === "active").length,
            pendingApprovals: bidsData.filter((b) => b.status === "pending").length,
            totalUsers: totalUsers,
            newUsersThisMonth: newUsersThisMonth
        })

        return stats;
    }

    // User management functions
    const handleUserAction = async (userId, action) => {
        try {
            const token = localStorage.getItem("access_token")
            await axios.post(
                `${config.API_BASE_URL}/admin/users/${userId}/${action}/`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            )
            fetchAdminData() // Refresh data
        } catch (error) {
            // console.error(`Error ${action} user:`, error)
        }
    }

    const handleBulkAction = async (action) => {
        try {
            const token = localStorage.getItem("access_token")
            await axios.post(
                `${config.API_BASE_URL}/admin/users/bulk/${action}/`,
                {
                    user_ids: selectedUsers,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            )
            setSelectedUsers([])
            fetchAdminData()
        } catch (error) {
            // console.error(`Error bulk ${action}:`, error)
        }
    }

    const exportData = async (type) => {
        try {
            const token = localStorage.getItem("access_token")
            const response = await axios.get(`${config.API_BASE_URL}/admin/export/${type}/`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
            })

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement("a")
            link.href = url
            link.setAttribute("download", `${type}_export_${new Date().toISOString().split("T")[0]}.csv`)
            document.body.appendChild(link)
            link.click()
            link.remove()
        } catch (error) {
            // console.error(`Error exporting ${type}:`, error)
        }
    }

    // Remove the static userStats calculation since we're now using state

    const eventStats = {
        totalEvents: events.length,
        upcomingEvents: events.filter((e) => new Date(e.date || e.start_date) > new Date()).length,
        pastEvents: events.filter((e) => new Date(e.date || e.start_date) < new Date()).length,
        activeEvents: events.filter((e) => e.status === "active").length,
    }

    const bidStats = {
        totalBids: bids.length,
        acceptedBids: bids.filter((b) => b.status === "accepted").length,
        pendingBids: bids.filter((b) => b.status === "pending").length,
        rejectedBids: bids.filter((b) => b.status === "rejected").length,
    }

    const getStatusBadge = (status, type = "user") => {
        const configs = {
            user: {
                active: "bg-green-100 text-green-800",
                inactive: "bg-red-100 text-red-800",
                banned: "bg-red-100 text-red-800",
            },
            event: {
                active: "bg-green-100 text-green-800",
                upcoming: "bg-blue-100 text-blue-800",
                completed: "bg-gray-100 text-gray-800",
                cancelled: "bg-red-100 text-red-800",
            },
            bid: {
                pending: "bg-yellow-100 text-yellow-800",
                accepted: "bg-green-100 text-green-800",
                rejected: "bg-red-100 text-red-800",
            },
        }

        const config = configs[type][status] || "bg-gray-100 text-gray-800"
        return (
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${config}`}>
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        )
    }

    if (loading || loadingData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
                <div className="text-center">
                    <div className="relative">
                        <div className="animate-spin rounded-full h-20 w-20 border-4 border-indigo-200 mx-auto"></div>
                        <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-indigo-600 mx-auto absolute top-0"></div>
                    </div>
                    <p className="mt-8 text-gray-700 text-xl font-semibold">Loading admin dashboard...</p>
                    <p className="mt-2 text-gray-500">Fetching system data</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
                <div className="text-center bg-white rounded-3xl shadow-2xl p-8 max-w-md">
                    <div className="mx-auto h-16 w-16 text-red-500 mb-4">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-red-600 mb-4">System Error</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors duration-200 font-semibold"
                    >
                        Retry Loading
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
            {/* Header */}
            <div className="bg-white shadow-xl border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-8">
                        <div>
                            <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                Admin Dashboard
                            </h1>
                            <p className="mt-3 text-gray-600 text-lg">System overview and management console</p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => exportData("users")}
                                className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-xl text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors duration-200"
                            >
                                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                                Export Data
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Navigation Tabs */}
                <div className="mb-8">
                    <div className="border-b border-gray-200 bg-white rounded-t-2xl">
                        <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                            {[
                                {
                                    id: "overview",
                                    name: "Overview",
                                    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                                },
                                {
                                    id: "users",
                                    name: "Users",
                                    icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
                                },
                                {
                                    id: "events",
                                    name: "Events",
                                    icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
                                },
                                {
                                    id: "bids",
                                    name: "Bids",
                                    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                                },
                                {
                                    id: "analytics",
                                    name: "Analytics",
                                    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                                },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                                        ? "border-indigo-500 text-indigo-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                        }`}
                                >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                                    </svg>
                                    <span>{tab.name}</span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Overview Tab */}
                {activeTab === "overview" && (
                    <div className="space-y-8">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="h-16 w-16 bg-green-100 rounded-2xl flex items-center justify-center">
                                            <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-6">
                                        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Revenue</p>
                                        <p className="text-3xl font-bold text-gray-900">
                                            KSh {systemStats.totalRevenue?.toLocaleString() || 0}
                                        </p>
                                        <p className="text-sm text-green-600 font-medium">+12% from last month</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="h-16 w-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                                            <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-6">
                                        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">New Users</p>
                                        <p className="text-3xl font-bold text-gray-900">{systemStats.newUsersThisMonth || 0}</p>
                                        <p className="text-sm text-blue-600 font-medium">This month</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="h-16 w-16 bg-purple-100 rounded-2xl flex items-center justify-center">
                                            <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-6">
                                        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Active Events</p>
                                        <p className="text-3xl font-bold text-gray-900">{systemStats.activeEvents || 0}</p>
                                        <p className="text-sm text-purple-600 font-medium">Currently running</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="h-16 w-16 bg-yellow-100 rounded-2xl flex items-center justify-center">
                                            <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="ml-6">
                                        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Pending Approvals</p>
                                        <p className="text-3xl font-bold text-gray-900">{systemStats.pendingApprovals || 0}</p>
                                        <p className="text-sm text-yellow-600 font-medium">Require attention</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {/* User Stats */}
                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">User Statistics</h3>
                                    <div className="bg-blue-100 p-3 rounded-2xl">
                                        <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">Total Users</span>
                                        <span className="text-2xl font-bold text-gray-900">{userStats.totalUsers}</span>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Service Providers</span>
                                            <span className="text-sm font-semibold text-gray-900">{userStats.serviceProviderCount}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Event Organizers</span>
                                            <span className="text-sm font-semibold text-gray-900">{userStats.eventOrganizerCount}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Event Attendees</span>
                                            <span className="text-sm font-semibold text-gray-900">{userStats.eventAttendeeCount}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Community Members</span>
                                            <span className="text-sm font-semibold text-gray-900">{userStats.communityMemberCount}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Event Stats */}
                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">Event Statistics</h3>
                                    <div className="bg-green-100 p-3 rounded-2xl">
                                        <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">Total Events</span>
                                        <span className="text-2xl font-bold text-gray-900">{eventStats.totalEvents}</span>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Upcoming</span>
                                            <span className="text-sm font-semibold text-gray-900">{eventStats.upcomingEvents}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Active</span>
                                            <span className="text-sm font-semibold text-gray-900">{eventStats.activeEvents}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Completed</span>
                                            <span className="text-sm font-semibold text-gray-900">{eventStats.pastEvents}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bid Stats */}
                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">Bid Statistics</h3>
                                    <div className="bg-purple-100 p-3 rounded-2xl">
                                        <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">Total Bids</span>
                                        <span className="text-2xl font-bold text-gray-900">{bidStats.totalBids}</span>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Accepted</span>
                                            <span className="text-sm font-semibold text-gray-900">{bidStats.acceptedBids}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Pending</span>
                                            <span className="text-sm font-semibold text-gray-900">{bidStats.pendingBids}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Rejected</span>
                                            <span className="text-sm font-semibold text-gray-900">{bidStats.rejectedBids}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">Recent Activity</h3>
                            <div className="space-y-4">
                                {recentActivity.length > 0 ? (
                                    recentActivity.slice(0, 10).map((activity, index) => (
                                        <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
                                            <div className="flex-shrink-0">
                                                <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                                    <svg
                                                        className="h-5 w-5 text-indigo-600"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900">{activity.description || "System activity"}</p>
                                                <p className="text-xs text-gray-500">
                                                    {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : "Recently"}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500">No recent activity to display</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === "users" && (
                    <div className="bg-white rounded-3xl shadow-lg border border-gray-100">
                        {/* Users Header */}
                        <div className="px-8 py-6 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                                <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
                                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search users..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                        />
                                        <svg
                                            className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                    </div>
                                    <select
                                        value={filterRole}
                                        onChange={(e) => setFilterRole(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                    >
                                        <option value="">All Roles</option>
                                        <option value="service_provider">Service Provider</option>
                                        <option value="event_organizer">Event Organizer</option>
                                        <option value="event_attendee">Event Attendee</option>
                                        <option value="community_member">Community Member</option>
                                    </select>
                                </div>
                            </div>

                            {/* Bulk Actions */}
                            {selectedUsers.length > 0 && (
                                <div className="mt-4 flex items-center space-x-3">
                                    <span className="text-sm text-gray-600">{selectedUsers.length} users selected</span>
                                    <button
                                        onClick={() => handleBulkAction("activate")}
                                        className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                                    >
                                        Activate
                                    </button>
                                    <button
                                        onClick={() => handleBulkAction("deactivate")}
                                        className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                                    >
                                        Deactivate
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Users Table */}
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left">
                                            <input
                                                type="checkbox"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedUsers(users.map((u) => u.id))
                                                    } else {
                                                        setSelectedUsers([])
                                                    }
                                                }}
                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            User
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Role
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last Login
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {users.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedUsers([...selectedUsers, user.id])
                                                        } else {
                                                            setSelectedUsers(selectedUsers.filter((id) => id !== user.id))
                                                        }
                                                    }}
                                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-12 w-12">
                                                        {user.profile_picture ? (
                                                            <img
                                                                className="h-12 w-12 rounded-full object-cover"
                                                                src={user.profile_picture || "/placeholder.svg"}
                                                                alt=""
                                                            />
                                                        ) : (
                                                            <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                                                                <span className="text-indigo-600 font-semibold text-lg">
                                                                    {user.first_name?.charAt(0) || user.email?.charAt(0)}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {user.first_name} {user.last_name}
                                                        </div>
                                                        <div className="text-sm text-gray-500">{user.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 capitalize">
                                                    {user.role?.replace("_", " ")}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.last_login ? new Date(user.last_login).toLocaleDateString() : "Never"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(user.is_active ? "active" : "inactive", "user")}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedUser(user)
                                                        setShowUserModal(true)
                                                    }}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                >
                                                    View
                                                </button>
                                                <button
                                                    onClick={() => handleUserAction(user.id, user.is_active ? "deactivate" : "activate")}
                                                    className={`${user.is_active ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}`}
                                                >
                                                    {user.is_active ? "Deactivate" : "Activate"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm text-gray-700">Show</span>
                                        <select
                                            value={pageCount}
                                            onChange={(e) => setPageCount(Number(e.target.value))}
                                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </select>
                                        <span className="text-sm text-gray-700">per page</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                                        >
                                            Previous
                                        </button>
                                        <span className="text-sm text-gray-700">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Events Tab */}
                {activeTab === "events" && (
                    <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Event Management</h2>
                        <div className="grid gap-6">
                            {events.map((event) => (
                                <div
                                    key={event.id}
                                    className="border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-200"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                                            <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                                            <div className="flex items-center space-x-4 mt-3">
                                                <span className="text-sm text-gray-500">
                                                    Date: {new Date(event.date || event.start_date).toLocaleDateString()}
                                                </span>
                                                <span className="text-sm text-gray-500">Organizer: {event.organizer_name || "Unknown"}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            {getStatusBadge(event.status || "active", "event")}
                                            <button className="text-indigo-600 hover:text-indigo-900 font-medium">Manage</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Bids Tab */}
                {activeTab === "bids" && (
                    <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Bid Management</h2>
                        <div className="grid gap-6">
                            {bids.map((bid) => (
                                <div
                                    key={bid.id}
                                    className="border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-200"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-gray-900">{bid.title}</h3>
                                            <p className="text-sm text-gray-600 mt-1">{bid.description}</p>
                                            <div className="flex items-center space-x-4 mt-3">
                                                <span className="text-sm text-gray-500">Amount: KSh {bid.price?.toLocaleString() || 0}</span>
                                                <span className="text-sm text-gray-500">Provider: {bid.provider_name || "Unknown"}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            {getStatusBadge(bid.status, "bid")}
                                            <button className="text-indigo-600 hover:text-indigo-900 font-medium">Review</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Analytics Tab */}
                {activeTab === "analytics" && (
                    <div className="space-y-8">
                        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">System Analytics</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
                                    <div className="h-64 bg-gray-100 rounded-xl flex items-center justify-center">
                                        <p className="text-gray-500">Chart placeholder - User growth over time</p>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trends</h3>
                                    <div className="h-64 bg-gray-100 rounded-xl flex items-center justify-center">
                                        <p className="text-gray-500">Chart placeholder - Revenue trends</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* User Detail Modal */}
            {showUserModal && selectedUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl">
                        <div className="px-8 py-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-gray-900">User Details</h3>
                                <button onClick={() => setShowUserModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="p-8">
                            <div className="flex items-center space-x-6 mb-6">
                                <div className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center">
                                    <span className="text-indigo-600 font-bold text-2xl">
                                        {selectedUser.first_name?.charAt(0) || selectedUser.email?.charAt(0)}
                                    </span>
                                </div>
                                <div>
                                    <h4 className="text-xl font-bold text-gray-900">
                                        {selectedUser.first_name} {selectedUser.last_name}
                                    </h4>
                                    <p className="text-gray-600">{selectedUser.email}</p>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 capitalize mt-2">
                                        {selectedUser.role?.replace("_", " ")}
                                    </span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Username</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedUser.username || "N/A"}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedUser.phone || "N/A"}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Date Joined</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {selectedUser.date_joined ? new Date(selectedUser.date_joined).toLocaleDateString() : "N/A"}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Last Login</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleDateString() : "Never"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminDashboard
